// ============================================================================
// view.breeding — the pig breeding Gantt, read AND written locally
// ============================================================================
// /pig/breeding rendered from THREE app_store blobs and owned none of them:
// ppp-breeding-v1 (its subject), ppp-boars-v1 (the display names every bar,
// card and form label falls back to) and ppp-breeders-v1 (read only to build a
// group's sow pool for the auto-merge into Boar 1). All three reached it
// through usePig() off App's boot transaction. `pig.breeding.core` reads them
// in one keyed select, which is what lets `breeding` retire from the frozen
// app_store consumer list in tests/fixtures/former-eager-dependencies.json.
//
// THIS FILE USED TO SAY THE SURFACE WAS PARTIAL, AND WHY. It named
// `legacy.context.pig.breeding` over two halves: `persistBreeding`, a PigEntry
// prop whose whole body was `sbSave('ppp-breeding-v1', nb)`, and
// `setBreedingCycles`, PigContext's raw setter called beside it "so the mirror
// stays in step for the code that still READS breedingCycles". Both halves are
// now retired, and the two arguments this file made against retiring them were
// each answered by a fact rather than by a workaround.
//
// HALF ONE — THE MIRROR HAD NO READER LEFT. The claim was four modules:
// App.tsx's own `resolveSire` closure, PigBatchesView, PigBatchPage and
// PigFeedView. Re-derived on this tree by enumerating every `usePig()`
// destructure in src/ and matching braces back from each call, the live set is
// EMPTY:
//
//   PigFeedView       no longer calls usePig() at all — /pig/feed reads
//                     pig.feed.core.
//   PigBatchesView    calls usePig() for the feeder FORM state and
//                     `setFeederGroups`. It destructures no cycle list.
//   PigBatchPage      same: form state only; its five pig values arrive on the
//                     `view` bundle PigBatchesView reads from pig.batches.core.
//   src/App.tsx       destructured `breedingCycles` for exactly one expression,
//                     `resolveSire`, which it handed to FarrowingView and
//                     SowsView as a prop. BOTH bound it as `_resolveSire` and
//                     called their own local resolver instead. The closure was
//                     dead, so App's read of the mirror was dead with it.
//
// A mirror write whose every reader has left is not a coupling; it is litter.
// `setBreedingCycles` is gone from BreedingView, and App's `breedingCycles` and
// `boarNames` destructures went with the closure that was their only consumer.
//
// HALF TWO — THE WRITE NEEDED APP FOR A HELPER, NOT FOR BOOT. This file argued
// the write "cannot move without changing PigEntry (which threads the prop) or
// App (which owns sbSave)". True, and not a reason: `sbSave(key, value)` reads
// nothing App's boot loads. What made it un-copyable was its SEMANTICS — three
// attempts, an 8s race each, never throws, and the header's Saving / Saved /
// Error indicator through AuthContext — which a route-local rewrite would have
// quietly changed for an operator on bad wifi. So the body moved instead of
// being duplicated: src/lib/appStoreBlobApi.ts holds it verbatim, App's `sbSave`
// is a two-line delegate to it, and this page calls the same function with its
// own key. src/lib/appStoreBlobApi.test.ts runs both implementations over a
// seven-scenario matrix (success, error×n, timeout×n) and asserts the complete
// observable trace — upsert arguments, status transitions, console records,
// delayed clears — is identical, with a two-attempt control proving the
// comparison can fail.
//
// The precedent for the destination is already in this repo and already
// `permanent`: src/lib/breedingTransferApi.ts performs the ppp-breeders-v1 and
// ppp-feeders-v1 upserts for view.weighinsessions, and
// routeMutationCatalog's own header calls a data-layer module "a write seam and
// a good one".
//
// ── WHY THE ROUTE MUTATION CATALOG IS STILL NOT THE ANSWER ─────────────────
//
// Unchanged, and worth keeping written down. A catalog entry's `invalidates`
// set names ROUTE RESOURCE IDS; `app_store` is one physical table holding every
// blob, so the completeness guard would require this entry to invalidate all 22
// catalog resources that read that table for a write that touches one key.
// `useRouteMutation` is `retry: false` and rejects, which is the opposite of
// the posture above. And `persistBreedCycle` runs off a 500 ms debounce while
// `useRouteMutation.run` AWAITS its invalidations, so every keystroke tick
// would block on a refetch of pig.breeding.core — the same objection
// layer.housings.detail records for `persistHousing`.
//
// WHAT ELSE MOVED WITH THE WRITE. `breedAutoSaveTimer` was a `useRef` hoisted
// into App and threaded down — a 500 ms debounce handle with APP lifetime, not
// mount lifetime, and no id in the closed RESIDUAL_LEGACY_IDS vocabulary to
// name it by. It is now a module-scope handle in src/pig/BreedingView.tsx,
// deliberately not a `useRef`: `showBreedForm` lives in PigContext, so the
// modal survives a remount, and a fresh per-mount ref would orphan a pending
// save instead of clearing it.
//
// WHAT THIS DECLARATION DOES NOT CLAIM. Releasing the surface is a separate
// decision with a ledger row, a resolver fence and a browser proof package;
// `/pig/breeding` is an EXACT route row, so that release needs no splat fence.
// Emptying `residualLegacy` says only what it says: this surface has no
// remaining dependency on App's legacy boot transaction or on an App-owned
// context value.
//
// WHAT IS DELIBERATELY NOT A RESIDUAL. The breeding FORM state — breedForm,
// showBreedForm, editBreedId, breedTlStart — still comes from usePig. It is UI
// state the provider happens to host, seeded from module-scope literals rather
// than from any boot read, so it is neither a render obligation nor a
// dependency on App's legacy transaction. Hoisting it out of the provider is a
// different change with a different reason.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.breeding',
  obligations: Object.freeze([Object.freeze({id: 'pig.breeding.core', owner: 'permanent'})]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
